// ─────────────────────────────────────────────────────────────────────────────
// Tour flow definitions
//
// Each flow is one "goal" the user can pick from the Play-icon launcher.
// A flow is an ordered list of steps. The TourRunner walks them one at a time,
// highlighting `target` and showing the popover, then advancing when the step's
// advance condition is met.
//
// Step shape:
//   {
//     route:      string   // the path this step expects to be on. If the current
//                          //   path differs, the runner waits until the user
//                          //   navigates here (by clicking the highlighted nav
//                          //   item), then shows the step. Use the path WITHOUT
//                          //   query string.
//     target:     string   // CSS selector of the element to highlight. Prefer
//                          //   [data-tour="…"]. The runner polls for it to mount
//                          //   (handles async pages) before highlighting.
//     title:      string
//     text:       string   // popover body (supports plain text)
//     advanceOn:  'click' | 'route' | 'next'   // how this step advances:
//                          //   'click' → user clicks the highlighted element
//                          //   'route' → user lands on the NEXT step's route
//                          //   'next'  → user presses the Next button (read-only)
//                          //   default: 'next'
//     placement?: 'top'|'bottom'|'left'|'right'   // optional hint; the coachmark
//                          //   auto-places in free space, so this is advisory only
//     allowInteraction?: boolean   // can the user click the highlighted element?
//                          //   default true (matches "user performs actions")
//   }
//
// Flow-level fields:
//   premium?: boolean   // true if this flow's destination page is gated by
//                        //   PlanRoute minPlan="premium" in App.js. The launcher
//                        //   shows a lock badge and opens UpgradeModal instead of
//                        //   starting the tour for free-plan users.
//
// To add a new flow: copy a block below, give it a unique id, list its steps,
// add a data-tour="…" attribute to each target element in the page, and add the
// flow id to a category in TOUR_CATEGORIES at the bottom.
// ─────────────────────────────────────────────────────────────────────────────

export const TOUR_FLOWS = {
  // ── Flow 1: Place your first trade ──────────────────────────────────────────
  // Multi-trigger flow: nav click → route change → in-page modal.
  'place-trade': {
    id: 'place-trade',
    label: 'Place your first trade',
    description: 'Walk through every field in the order ticket before placing',
    icon: 'TrendingUp',
    steps: [
      {
        route: '/',
        target: '[data-tour="nav-trade"]',
        title: 'Start here — the Trade tab',
        text: 'This is where you find stocks and place orders. Tap it to continue.',
        advanceOn: 'route',           // advances when we land on /trade
        placement: 'top',
      },
      {
        route: '/trade',
        target: '[data-tour="stock-search"]',
        // advanceTarget: highlight the search box, but advance when the user
        // taps ANY result's BUY button (they mount after typing and open the
        // order ticket). Matches the stable per-result testid so any result
        // works, not just the first. Delegated click handles late renders.
        advanceTarget: '[data-testid^="buy-btn-"]',
        title: 'Search and open a Buy order',
        text: 'Type a symbol or company name — e.g. "RELIANCE". When the result appears below, tap its green BUY (B) button to open the order ticket.',
        advanceOn: 'click',
        placement: 'top',
      },
      {
        route: '/trade',
        target: '[data-tour="order-product-type"]',
        title: 'Intraday or Swing?',
        text: 'Intraday means you must close by 3:30 PM today. Swing (CNC) lets you hold overnight for days or weeks. For F&O it shows as "Intraday" or "Carry". Pick one to continue.',
        advanceOn: 'click',
        advanceTarget: '[data-tour="order-product-type"] button',
        placement: 'bottom',
      },
      {
        route: '/trade',
        target: '[data-tour="order-mode-qty"]',
        title: 'Market vs Limit',
        text: 'Market fills immediately at the live price — good for liquid stocks. Limit fills only at your chosen price or better — use it to control entry. After picking, adjust quantity with + / −.',
        advanceOn: 'click',
        advanceTarget: '[data-tour="order-mode-btn"]',
        placement: 'bottom',
      },
      {
        route: '/trade',
        target: '[data-tour="order-sl-target"]',
        title: 'Stop Loss & Target',
        text: 'Stop Loss exits the trade automatically if price hits this level — it caps your downside. Target locks in profit. Together they define your Risk:Reward ratio. Intraday requires an SL; Swing strongly recommends one. Enter your levels, then tap Next.',
        advanceOn: 'next',
        placement: 'bottom',
        allowInteraction: true,
      },
      {
        route: '/trade',
        target: '[data-tour="order-journal-reason"]',
        title: 'Why this trade?',
        text: 'Pick the primary reason — Strategy, Scanner, Technical, or others. This is required. You can also add optional technical tags like Breakout or RSI. Writing your reason builds self-awareness and feeds your discipline score over time. Tap a reason chip to continue.',
        advanceOn: 'click',
        advanceTarget: '[data-testid^="reason-"]',
        placement: 'bottom',
      },
      {
        route: '/trade',
        target: '[data-tour="order-confidence"]',
        title: 'Confidence score',
        text: 'Rate your conviction from 1 to 10. Low confidence (1–3) is a warning signal — what is your edge? High (7–10) — write your thesis in the notes below. TradeReflex tracks confidence vs outcome to reveal how well-calibrated you are. Drag the slider, then tap Next.',
        advanceOn: 'next',
        placement: 'bottom',
        allowInteraction: true,
      },
      {
        route: '/trade',
        target: '[data-tour="order-risk-panel"]',
        title: 'Risk vs your profile',
        text: 'This compares your potential max loss — calculated from your SL distance × quantity — against the limit your risk profile recommends. Green means you are within limit. Orange is over. Red means seriously overexposed — reduce quantity or tighten your SL. No SL = risk unknown.',
        advanceOn: 'next',
        placement: 'top',
        allowInteraction: false,
      },
      {
        route: '/trade',
        target: '[data-tour="order-place-btn"]',
        title: 'Place the trade',
        text: 'When you are ready, tap the BUY button to place the order. This is paper trading — no real money involved. After placing, the position appears in your open trades and your discipline score updates when it closes. Tap Finish to end the tour, then place when you are ready.',
        advanceOn: 'next',
        placement: 'top',
        allowInteraction: false,
      },
    ],
  },

  // ── Flow 2: Check your analytics ────────────────────────────────────────────
  // Simple navigate-and-explain flow (no in-page modal).
  'view-analytics': {
    id: 'view-analytics',
    label: 'Review your trading analytics',
    description: 'See your performance and behaviour stats',
    icon: 'BarChart2',
    premium: true,
    steps: [
      {
        route: '/',
        target: '[data-tour="nav-discover"]',
        title: 'Open the Discover tab',
        text: 'Analytics, scanners, learning and more live under Discover. Tap it to continue.',
        advanceOn: 'route',
        placement: 'top',
      },
      {
        route: '/discover',
        target: '[data-tour="discover-analytics"]',
        title: 'Go to Analytics',
        text: 'This opens your trading analytics — win rate, P&L, and your behaviour score over time.',
        advanceOn: 'route',
        placement: 'bottom',
      },
      {
        route: '/trading-analytics',
        target: '[data-tour="analytics-summary"]',
        title: 'Your performance at a glance',
        text: 'These cards summarise how you are trading. Come back here regularly — the goal is consistency, not a single big win.',
        advanceOn: 'next',
        placement: 'bottom',
      },
    ],
  },

  // ── Flow 3: Discover Scanner — Patterns ──────────────────────────────────────
  'discover-scanner-patterns': {
    id: 'discover-scanner-patterns',
    label: 'Find breakout patterns',
    description: 'Use the scanner to spot stocks forming key chart patterns',
    icon: 'ScanLine',
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-scanners"]',
        title: 'Open the Scanners',
        text: 'Scanners surface stocks forming known chart patterns. Tap this tile to open them.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/scanners',
        target: '[data-tour="scanner-tab-bar"]',
        title: 'Patterns, Signals, Screener',
        text: 'Three tabs — Patterns spots chart formations, Signals flags momentum triggers, Screener lets you filter by your own criteria. Tap the Patterns tab to continue.',
        advanceOn: 'click',          // user taps the tab bar
        placement: 'bottom',
      },
      {
        route: '/scanners',
        target: '[data-tour="scanner-timeframe"]',
        title: 'Swing vs Intraday',
        text: 'Switch between Swing (multi-day setups) and Intraday (same-session setups) to match your trading style. Tap either option.',
        advanceOn: 'click',          // user taps the timeframe toggle
        placement: 'bottom',
      },
      {
        route: '/scanners',
        target: '[data-tour="scanner-pattern-filter"]',
        title: 'Filter by pattern type',
        text: 'Tap a pattern chip — Breakout, Reversal, or Continuation — to narrow down to only the setups you trade.',
        advanceOn: 'click',          // user taps a filter chip
        placement: 'bottom',
      },
      {
        route: '/scanners',
        target: '[data-tour="scanner-pattern-cards"]',
        title: 'Pattern results',
        text: 'Each card shows the stock, the formation found, and key levels. Tap any card to open the chart and place a trade.',
        advanceOn: 'next',           // read-only — results grid, nothing to click to advance
        placement: 'top',
      },
    ],
  },

  // ── Flow 4: Discover Scanner — Signals ───────────────────────────────────────
  'discover-scanner-signals': {
    id: 'discover-scanner-signals',
    label: 'Find momentum signals',
    description: 'Use the Signals tab to find stocks with live momentum triggers',
    icon: 'Zap',
    steps: [
      {
        route: '/scanners',
        target: '[data-tour="scanner-tab-bar"]',
        title: 'Switch to Signals',
        text: 'Signals fire when a stock crosses a technical threshold — like a MACD crossover or RSI reversal. Tap the Signals tab now.',
        advanceOn: 'click',          // user taps the Signals tab
        placement: 'bottom',
      },
      {
        route: '/scanners',
        target: '[data-tour="scanner-timeframe"]',
        title: 'Match your timeframe',
        text: 'Set Swing for overnight holds or Intraday for same-session momentum plays. Tap to switch.',
        advanceOn: 'click',          // user taps the timeframe toggle
        placement: 'bottom',
      },
      {
        route: '/scanners',
        target: '[data-tour="scanner-confluence"]',
        title: 'Confluence filter',
        text: 'Confluence means multiple signals aligning on the same stock. Higher confluence = stronger setup. Tap to adjust the minimum.',
        advanceOn: 'click',          // user taps the confluence filter
        placement: 'bottom',
      },
      {
        route: '/scanners',
        target: '[data-tour="scanner-signal-cards"]',
        title: 'Signal results',
        text: 'Each card shows the triggered signal, the stock, and key indicators (RSI, volume, trend). Tap any card to view the chart.',
        advanceOn: 'next',           // read-only results list
        placement: 'top',
      },
    ],
  },

  // ── Flow 5: Run a Backtest ────────────────────────────────────────────────────
  'run-backtest': {
    id: 'run-backtest',
    label: 'Run a backtest',
    description: 'Test a strategy on historical data before risking real money',
    icon: 'FlaskConical',
    premium: true,
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-backtest"]',
        title: 'Open Backtest Lab',
        text: 'Backtest Lab lets you test your entry and exit rules on historical market data — no real money at risk. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/profile',
        target: '[data-tour="backtest-strategy-form"]',
        title: 'Define your strategy',
        text: 'Set the indicator rules, entry condition, stop loss, and target. The more specific your rules, the more reliable the results.',
        advanceOn: 'next',           // form — user fills it at their own pace
        placement: 'bottom',
      },
      {
        route: '/profile',
        target: '[data-tour="backtest-run-btn"]',
        title: 'Run the backtest',
        text: 'Hit Run to test your strategy across hundreds of historical scenarios. Results appear below in seconds — tap Run now, then tap Next once results load.',
        advanceOn: 'next',           // backtest takes time; user advances manually once results appear
        placement: 'bottom',
        allowInteraction: true,
      },
      {
        route: '/profile',
        target: '[data-tour="backtest-results"]',
        title: 'Read the results',
        text: 'Check win rate, average R:R, max drawdown, and the equity curve. A strategy with consistent R:R beats one with a high win rate but poor risk control.',
        advanceOn: 'next',           // read-only results
        placement: 'top',
      },
    ],
  },

  // ── Flow 6: Read Market Regime ────────────────────────────────────────────────
  'read-market-regime': {
    id: 'read-market-regime',
    label: 'Read the market regime',
    description: 'Understand the current market environment before you trade',
    icon: 'Activity',
    premium: true,
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-market-analysis"]',
        title: 'Open Market Regime',
        text: 'The regime tells you whether the market is trending, ranging, or volatile — your entire playbook changes based on this. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/market-analysis',
        target: '[data-tour="regime-hero"]',
        title: 'Current regime at a glance',
        text: 'The hero band shows today\'s market regime — Trending, Range-bound, or Volatile — with a short playbook note.',
        advanceOn: 'next',           // read-only hero band
        placement: 'bottom',
      },
      {
        route: '/market-analysis',
        target: '[data-tour="regime-tabs"]',
        title: 'Change the period',
        text: 'Switch between Daily, Weekly, and Monthly views to see whether the regime is short-term noise or a sustained trend. Tap a tab.',
        advanceOn: 'click',          // user taps a period tab
        placement: 'bottom',
      },
      {
        route: '/market-analysis',
        target: '[data-tour="regime-summary-cards"]',
        title: 'Four key KPIs',
        text: 'Breadth, momentum, volatility, and institutional flow — these four numbers tell you how strong the current regime really is.',
        advanceOn: 'next',           // read-only KPI grid
        placement: 'bottom',
      },
      {
        route: '/market-analysis',
        target: '[data-tour="regime-day-cards"]',
        title: 'Today\'s action plan',
        text: 'This card translates the regime into concrete rules for today — what setups to favour, what to avoid, and why.',
        advanceOn: 'next',           // read-only action card
        placement: 'top',
      },
    ],
  },

  // ── Flow 7: View Trade Analytics ─────────────────────────────────────────────
  'view-trade-analytics': {
    id: 'view-trade-analytics',
    label: 'Analyse your trade performance',
    description: 'Dig into your win rate, P&L, and behaviour metrics',
    icon: 'TrendingUp',
    premium: true,
    steps: [
      {
        route: '/trading-analytics',
        target: '[data-tour="analytics-summary"]',
        title: 'Performance overview',
        text: 'Your total P&L, win rate, and trade count at the top. This is your trading scoreboard.',
        advanceOn: 'next',           // read-only summary cards
        placement: 'bottom',
      },
      {
        route: '/trading-analytics',
        target: '[data-tour="analytics-timeframe"]',
        title: 'Change the timeframe',
        text: 'Filter by 7 days, 30 days, or all time. Look at 30-day windows to spot improving or deteriorating patterns. Tap a period.',
        advanceOn: 'click',          // user taps a timeframe chip
        placement: 'bottom',
      },
      {
        route: '/trading-analytics',
        target: '[data-tour="analytics-metrics-grid"]',
        title: 'Detailed metrics',
        text: 'Average hold time, trades by instrument, win streaks, loss streaks — the patterns here reveal your real trading behaviour.',
        advanceOn: 'next',           // read-only metrics grid
        placement: 'bottom',
      },
      {
        route: '/trading-analytics',
        target: '[data-tour="analytics-performance"]',
        title: 'Win vs Loss size',
        text: 'Average win and average loss compared. You want avg win ≥ 1.5× avg loss. If not, your risk management needs work.',
        advanceOn: 'next',           // read-only stat card
        placement: 'bottom',
      },
      {
        route: '/trading-analytics',
        target: '[data-tour="analytics-equity-curve"]',
        title: 'Equity curve',
        text: 'A rising equity curve with small drawdowns = consistent process. A jagged curve with big drops = sizing or discipline issue.',
        advanceOn: 'next',           // read-only chart
        placement: 'top',
      },
    ],
  },

  // ── Flow 8: Read Discipline Score ─────────────────────────────────────────────
  'read-discipline-score': {
    id: 'read-discipline-score',
    label: 'Understand your discipline score',
    description: 'Learn what your behaviour score means and how to improve it',
    icon: 'Target',
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-discipline"]',
        title: 'Open Discipline',
        text: 'Your discipline score measures how consistently you follow your own rules — not whether you made money. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/discipline',
        target: '[data-tour="discipline-score-card"]',
        title: 'Your behaviour score',
        text: 'The arc shows your current score (0–100). The spectrum bar shows which tier you are in — Minimum, Standard, Advanced, or Elite.',
        advanceOn: 'next',           // read-only score card
        placement: 'bottom',
      },
      {
        route: '/discipline',
        target: '[data-tour="discipline-benchmark"]',
        title: 'Peer benchmarking',
        text: 'How do you compare to other traders? Seeing where you stand relative to your peers is a powerful motivator.',
        advanceOn: 'next',           // read-only benchmark card
        placement: 'bottom',
      },
      {
        route: '/discipline',
        target: '[data-tour="discipline-behavior-metrics"]',
        title: 'Behaviour breakdown',
        text: 'Which specific behaviours are dragging your score? Each metric shows your current level and what to fix first.',
        advanceOn: 'next',           // read-only metrics grid
        placement: 'bottom',
      },
      {
        route: '/discipline',
        target: '[data-tour="discipline-pnl-impact"]',
        title: 'Score → Capital limit',
        text: 'Your discipline score directly controls your daily capital limit. Higher score = more capital unlocked. Improve the process, not just the P&L.',
        advanceOn: 'next',           // read-only impact card
        placement: 'top',
      },
    ],
  },

  // ── Flow 9: Set Risk Profile ──────────────────────────────────────────────────
  'set-risk-profile': {
    id: 'set-risk-profile',
    label: 'Set your risk profile',
    description: 'Calibrate risk tolerance so position sizing is personalised',
    icon: 'ShieldCheck',
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-risk-profile"]',
        title: 'Open Risk Profile',
        text: 'Your risk profile tells the app how much risk you can handle — it adjusts position sizing recommendations for you personally. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/risk-profile',
        target: '[data-tour="risk-score-card"]',
        title: 'Your risk score',
        text: 'The score summarises your risk capacity, knowledge, and behaviour into a single number from 1–100.',
        advanceOn: 'next',           // read-only score card
        placement: 'bottom',
      },
      {
        route: '/risk-profile',
        target: '[data-tour="risk-score-breakdown"]',
        title: 'What drives your score',
        text: 'Three components: Capacity (how much loss you can absorb), Knowledge (experience level), and Behaviour (emotional control). Lowest component limits the overall score.',
        advanceOn: 'next',           // read-only breakdown bars
        placement: 'bottom',
      },
      {
        route: '/risk-profile',
        target: '[data-tour="risk-recommendations"]',
        title: 'Personalised limits',
        text: 'Based on your score, these are the recommended Risk per Trade %, trading style, and max trades per day for you.',
        advanceOn: 'next',           // read-only recommendation cards
        placement: 'bottom',
      },
      {
        route: '/risk-profile',
        target: '[data-tour="risk-adherence"]',
        title: 'Adherence tracking',
        text: 'This shows how often you actually follow the recommended limits. Consistent adherence improves your behaviour score over time.',
        advanceOn: 'next',           // read-only adherence section
        placement: 'top',
      },
    ],
  },

  // ── Flow 10: Use Risk Calculator ─────────────────────────────────────────────
  'use-risk-calculator': {
    id: 'use-risk-calculator',
    label: 'Size a position with the calculator',
    description: 'Plan how many shares or lots to buy based on your risk budget',
    icon: 'Calculator',
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-calculator"]',
        title: 'Open the Calculator',
        text: 'The calculator tells you exactly how many shares or lots to buy for any trade based on your account size and risk tolerance. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/calculator',
        target: '[data-tour="calc-capital-summary"]',
        title: 'Your limits at a glance',
        text: 'Daily capital limit, risk per trade, and maximum trades allowed today — all derived from your discipline score and risk profile.',
        advanceOn: 'next',           // read-only summary widget
        placement: 'bottom',
      },
      {
        route: '/calculator',
        target: '[data-tour="calc-overview"]',
        title: 'Plan overview',
        text: 'Set your account size, number of trades planned today, and risk percentage. The allocation bars update live as you adjust.',
        advanceOn: 'click',          // user interacts with the inputs
        placement: 'bottom',
      },
      {
        route: '/calculator',
        target: '[data-tour="calc-trade-slots"]',
        title: 'Individual trade slots',
        text: 'Each slot shows the entry, stop loss, and quantity to buy. Enter your price levels and the quantity calculates automatically.',
        advanceOn: 'click',          // user taps into a trade slot
        placement: 'bottom',
      },
      {
        route: '/calculator',
        target: '[data-tour="calc-plan-summary"]',
        title: 'Best and worst case',
        text: 'If all trades hit target vs all hit stop loss — this is your realistic P&L range for the day. Never risk more than you can absorb.',
        advanceOn: 'next',           // read-only summary
        placement: 'top',
      },
    ],
  },

  // ── Flow 11: Create an Algo Flow ──────────────────────────────────────────────
  'create-algo-flow': {
    id: 'create-algo-flow',
    label: 'Create an algo flow',
    description: 'Build and backtest a rule-based strategy with the Algo Flows engine',
    icon: 'FlaskConical',
    premium: true,
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-algo-flows"]',
        title: 'Open Algo Flows',
        text: 'Algo Flows lets you build rule-based strategies and see how they would have performed on historical data. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/algo-flows',
        target: '[data-tour="algo-dashboard"]',
        title: 'Your strategy dashboard',
        text: 'Total active strategies, capital allocated, combined P&L, and total trades — your algo portfolio at a glance.',
        advanceOn: 'next',           // read-only KPI grid
        placement: 'bottom',
      },
      {
        route: '/algo-flows',
        target: '[data-tour="algo-strategies-list"]',
        title: 'Strategy list',
        text: 'All your saved strategies are here. Each row shows the name, status (active / paused), and latest performance. Tap one to open its detail.',
        advanceOn: 'click',          // user taps a strategy row
        placement: 'right',
      },
      {
        route: '/algo-flows',
        target: '[data-tour="algo-create-btn"]',
        title: 'Create a new strategy',
        text: 'Tap New Strategy to define your entry rules, exit rules, and risk parameters. No coding required.',
        advanceOn: 'click',          // user taps the create button
        placement: 'bottom',
      },
      {
        route: '/algo-flows',
        target: '[data-tour="algo-backtest-panel"]',
        title: 'Backtest before you run it live',
        text: 'After saving your strategy, run a backtest here to see its historical win rate, drawdown, and R:R before committing any capital.',
        advanceOn: 'next',           // read-only backtest panel
        placement: 'top',
      },
    ],
  },

  // ── Flow 12: Do an ACT Challenge ─────────────────────────────────────────────
  'do-act-challenge': {
    id: 'do-act-challenge',
    label: 'Do an ACT challenge',
    description: 'Practice trade decisions on real historical scenarios and get scored',
    icon: 'Zap',
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-act"]',
        title: 'Open ACT Practice',
        text: 'ACT gives you real historical trade setups and scores your decision process — not just whether the trade made money. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/act',
        target: '[data-tour="act-daily-progress"]',
        title: 'Daily progress',
        text: 'Complete 5 Swing and 5 Intraday scenarios each day. Progress bars here track today\'s count.',
        advanceOn: 'next',           // read-only progress bars
        placement: 'bottom',
      },
      {
        route: '/act',
        target: '[data-tour="act-trade-type-toggle"]',
        title: 'Swing vs Intraday',
        text: 'Toggle between Swing scenarios (15-day outcomes) and Intraday scenarios (same-session outcomes). Both build different skills. Tap to switch.',
        advanceOn: 'click',          // user taps the toggle
        placement: 'bottom',
      },
      {
        route: '/act',
        target: '[data-tour="act-scenario-cards"]',
        title: 'Today\'s scenarios',
        text: 'Each card is a real stock on a specific date. Tap one to open the chart, read the technical context, and make your decision.',
        advanceOn: 'click',          // user taps a scenario card
        placement: 'bottom',
      },
      {
        route: '/act',
        target: '[data-tour="act-quick-actions"]',
        title: 'Review history and risk profile',
        text: 'History shows your past scores and decision breakdown. Risk Profile links your ACT performance to your real trading limits.',
        advanceOn: 'next',           // read-only quick action buttons
        placement: 'top',
      },
    ],
  },

  // ── Flow 13: Start a Learning Path ───────────────────────────────────────────
  'start-learning-path': {
    id: 'start-learning-path',
    label: 'Start a learning path',
    description: 'Work through the 13-level structured trading curriculum',
    icon: 'BookOpen',
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-learning"]',
        title: 'Open Learning',
        text: 'The learning module has 13 levels covering market basics to advanced risk management. Each level builds on the last. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/learning',
        target: '[data-tour="learning-all-levels"]',
        title: 'All 13 levels',
        text: 'The full curriculum is here — 13 levels from market basics to advanced risk management. Later levels unlock as you complete earlier ones. Tap any level tile to open it.',
        advanceOn: 'click',          // user taps a level tile to advance
        advanceTarget: '[data-tour="learning-all-levels"] button',
        placement: 'bottom',
      },
      {
        route: '/learning',
        target: '[data-tour="learning-progress-banner"]',
        title: 'Level progress',
        text: 'This bar shows how far you are through the current level — lessons completed out of total. Finish all lessons to unlock the next level.',
        advanceOn: 'next',
        placement: 'bottom',
      },
      {
        route: '/learning',
        target: '[data-tour="learning-modules-list"]',
        title: 'Lessons in this level',
        text: 'Each lesson covers one concept — tap any card to start reading. Green badge means done, number badge means pending. Tap a lesson to begin.',
        advanceOn: 'click',
        advanceTarget: '[data-tour="learning-modules-list"] button',
        placement: 'bottom',
      },
    ],
  },

  // ── Flow 14: Browse Trade History ─────────────────────────────────────────────
  'browse-trade-history': {
    id: 'browse-trade-history',
    label: 'Review your trade history',
    description: 'See every completed trade and share your best P&L',
    icon: 'History',
    steps: [
      {
        route: '/discover',
        target: '[data-tour="discover-trade-history"]',
        title: 'Open Trade History',
        text: 'Every trade you have closed is here with entry, exit, P&L, and strategy tags. Tap to open.',
        advanceOn: 'route',          // tile tap navigates → advance on arrival
        placement: 'bottom',
      },
      {
        route: '/trade-history',
        target: '[data-tour="history-header"]',
        title: 'Total trades',
        text: 'The header shows your total completed trade count. You can also arrive here filtered to a specific symbol from the analytics page.',
        advanceOn: 'next',           // read-only header
        placement: 'bottom',
      },
      {
        route: '/trade-history',
        target: '[data-tour="history-trade-list"]',
        title: 'Trade cards',
        text: 'Each card shows the symbol, entry and exit prices, hold duration, and final P&L. Tap any trade to open its full analysis.',
        advanceOn: 'click',          // user taps a trade card
        placement: 'bottom',
      },
      {
        route: '/trade-history',
        target: '[data-tour="history-share-pnl"]',
        title: 'Share your P&L',
        text: 'Tap Share P&L to post a summary to the community feed — shows win rate, total P&L, and best trade.',
        advanceOn: 'click',          // user taps the Share P&L button
        placement: 'bottom',
      },
    ],
  },

  // ── Flow 15: Trade Planning Guide ────────────────────────────────────────────
  'trade-planning-guide': {
    id: 'trade-planning-guide',
    label: 'End-to-end trade planning',
    description: 'Walk through every step from pre-market prep to order placement',
    icon: 'Map',
    steps: [
      {
        route: '/trade-planning',
        target: '[data-tour="trade-plan-instrument-tabs"]',
        title: 'Choose your instrument',
        text: 'Start by picking your instrument — Intraday Equity, Futures, or Options. Each has different risk sizing rules. Tap one to see the example adapt.',
        advanceOn: 'click',          // user taps an instrument tab
        placement: 'bottom',
      },
      {
        route: '/trade-planning',
        target: '[data-tour="trade-plan-step-1"]',
        title: 'Step 1: Check global cues',
        text: 'Before market opens, check where SGX Nifty, S&P 500 futures, and Nikkei closed. These set your directional bias for the day. Tap the step to expand it.',
        advanceOn: 'click',          // user taps the step card to open it
        placement: 'bottom',
      },
      {
        route: '/trade-planning',
        target: '[data-tour="trade-plan-step-2"]',
        title: 'Step 2: Read FII / DII flow',
        text: 'Institutional money tells you conviction. DIIs absorbing FII selling = dip support. Both selling = avoid longs. Tap to expand.',
        advanceOn: 'click',          // user taps the step card
        placement: 'bottom',
      },
      {
        route: '/trade-planning',
        target: '[data-tour="trade-plan-step-3"]',
        title: 'Step 3: Read the regime',
        text: 'Gap size and the first candle define the day type — trend, range, or volatile. This changes your entire playbook. Never skip this step. Tap to expand.',
        advanceOn: 'click',          // user taps the step card
        placement: 'bottom',
      },
      {
        route: '/trade-planning',
        target: '[data-tour="trade-plan-step-4"]',
        title: 'Step 4: Find your setup',
        text: 'Use the scanners to find a stock at a known level with elevated volume. The chart must tell a clear story — if in doubt, skip. Tap to expand.',
        advanceOn: 'click',          // user taps the step card
        placement: 'bottom',
      },
      {
        route: '/trade-planning',
        target: '[data-tour="trade-plan-step-5"]',
        title: 'Step 5 & 6: Size and place',
        text: 'Risk a fixed rupee amount — not a fixed lot. Calculate quantity from your stop distance, then write your reason before you tap Place Order.',
        advanceOn: 'next',           // last step — read-only wrap-up
        placement: 'top',
      },
    ],
  },
};

// ── Categories for the grouped launcher UI ───────────────────────────────────
export const TOUR_CATEGORIES = [
  {
    id: 'trading',
    label: 'Trading',
    flows: ['place-trade', 'browse-trade-history', 'use-risk-calculator', 'trade-planning-guide'],
  },
  {
    id: 'analysis',
    label: 'Analysis',
    flows: [
      'view-analytics',
      'view-trade-analytics',
      'discover-scanner-patterns',
      'discover-scanner-signals',
      'read-market-regime',
      'create-algo-flow',
      'run-backtest',
    ],
  },
  {
    id: 'learning',
    label: 'Learning',
    flows: ['start-learning-path', 'do-act-challenge'],
  },
  {
    id: 'account',
    label: 'Account & Profile',
    flows: ['read-discipline-score', 'set-risk-profile'],
  },
];
